import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon, Close as CloseIcon } from '@mui/icons-material';
import { event } from '../analytics';

const SUBMIT_CONTACT_SALES = gql`
  mutation SubmitContactSales($input: SubmitContactSalesInput!) {
    submitContactSales(input: $input) {
      success
      message
    }
  }
`;

const PARTNERS_COPY =
  "Verit Compliance is interested in early customers and strategic partners who are open to providing product feedback and usage insight in exchange for a heavily discounted price. If that sounds like you, we'd love to hear from you.";

export type DemoModalVariant = 'welcome' | 'reminder';

interface DemoContactModalProps {
  open: boolean;
  onClose: () => void;
  variant: DemoModalVariant;
}

const DemoContactModal: React.FC<DemoContactModalProps> = ({ open, onClose, variant }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitContactSales, { loading, data, error }] = useMutation(SUBMIT_CONTACT_SALES);

  const success = data?.submitContactSales?.success;
  const message = data?.submitContactSales?.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await submitContactSales({
      variables: { input: { name, email, companyName, notes } },
    });
    if (result.data?.submitContactSales?.success) {
      event('sales_outreach_submit', { event_category: 'sales', event_action: 'outreach_submit' });
    }
  };

  const isWelcome = variant === 'welcome';
  const title = isWelcome ? 'Welcome to the Verit Compliance Demo' : 'Still interested?';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.05)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.92) 100%)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: isWelcome ? '#134e4a' : '#92400e',
          pt: 3,
          pb: 0,
          fontSize: '1.35rem',
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {isWelcome && (
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2 }}>
            You're exploring a read-only sandbox with pre-loaded AI safety and compliance data.
            Feel free to browse the dashboard, sequences, results, and alerts.
          </Typography>
        )}

        <Box
          sx={{
            bgcolor: 'rgba(240, 253, 250, 0.9)',
            border: '1px solid rgba(153, 246, 228, 0.6)',
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="body1" sx={{ color: '#134e4a' }}>
            {PARTNERS_COPY}
          </Typography>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={onClose}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}
        {data && !success && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <form onSubmit={handleSubmit} id="demo-contact-form">
          <TextField
            fullWidth
            size="small"
            label="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            margin="dense"
            variant="outlined"
          />
          <TextField
            fullWidth
            size="small"
            label="Your email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="dense"
            variant="outlined"
          />
          <TextField
            fullWidth
            size="small"
            label="Your company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            margin="dense"
            variant="outlined"
          />
          <TextField
            fullWidth
            size="small"
            label="Additional notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            margin="dense"
            variant="outlined"
            placeholder="Use case, timeline, or questions..."
          />
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} variant="outlined" color="inherit" startIcon={<CloseIcon />}>
          Maybe later
        </Button>
        <Button
          type="submit"
          form="demo-contact-form"
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #134e4a 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #2d4a6f 0%, #1e5f54 100%)' },
          }}
        >
          {loading ? 'Sending...' : 'Send to Sales'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoContactModal;
